// Pure helpers for the Rewards admin page (no React, unit-tested).

export const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

// Split pasted text or an uploaded CSV into non-empty rows of trimmed cells.
function rowsOf(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.split(/[,;\t]/).map((c) => c.trim().replace(/^"|"$/g, "")))
    .filter((cells) => cells.some((c) => c !== ""));
}

// A header row is one whose first cell is not an address (e.g. "wallet").
const isHeader = (cells) => !EVM_ADDRESS.test(cells[0] || "") && /[a-z]/i.test(cells[0] || "") && !/^0x/i.test(cells[0] || "");

// Wallet list: one per line, or a CSV whose first column is the wallet.
// Returns { wallets (unique, lowercased), invalid: [raw], duplicates: n }.
export function parseWalletList(text) {
  const rows = rowsOf(text);
  if (rows.length && isHeader(rows[0])) rows.shift();
  const seen = new Set();
  const wallets = [];
  const invalid = [];
  let duplicates = 0;
  for (const cells of rows) {
    const raw = cells[0];
    if (!EVM_ADDRESS.test(raw)) { invalid.push(raw); continue; }
    const w = raw.toLowerCase();
    if (seen.has(w)) { duplicates += 1; continue; }
    seen.add(w);
    wallets.push(w);
  }
  return { wallets, invalid, duplicates };
}

// Snapshot balances: "wallet,balance" per line (header optional). The first
// row for a wallet wins; later rows for the same wallet count as duplicates.
// Returns { balances: [{ wallet, balance }], invalid: [raw line], duplicates: n }.
export function parseBalances(text) {
  const rows = rowsOf(text);
  if (rows.length && isHeader(rows[0])) rows.shift();
  const seen = new Set();
  const balances = [];
  const invalid = [];
  let duplicates = 0;
  for (const cells of rows) {
    const [raw, amt] = cells;
    const balance = Number(String(amt || "").replace(/_/g, ""));
    if (!EVM_ADDRESS.test(raw || "") || !Number.isFinite(balance) || balance <= 0) {
      invalid.push(cells.join(","));
      continue;
    }
    const wallet = raw.toLowerCase();
    if (seen.has(wallet)) { duplicates += 1; continue; }
    seen.add(wallet);
    balances.push({ wallet, balance });
  }
  return { balances, invalid, duplicates };
}

// The backend credits each wallet round(total_points * balance / sum(balances)).
// This mirrors it so the admin sees the split before submitting.
export function splitSnapshot(totalPoints, balances) {
  const total = Number(totalPoints);
  const sum = balances.reduce((s, b) => s + Number(b.balance), 0);
  if (!Number.isFinite(total) || total <= 0 || sum <= 0) return { rows: [], sum, credited: 0 };
  const rows = balances.map((b) => {
    const share = Number(b.balance) / sum;
    return { wallet: b.wallet, balance: Number(b.balance), share, points: Math.round(total * share) };
  });
  return { rows, sum, credited: rows.reduce((s, r) => s + r.points, 0) };
}

// CSV with every cell quoted only when needed.
export function toCsv(rows, columns) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(typeof c.value === "function" ? c.value(r) : r[c.value])).join(","));
  return [head, ...body].join("\n") + "\n";
}

export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

// Bulk codes come back as strings or as objects; normalise to objects.
export function normaliseCodes(codes, fallback = {}) {
  return (Array.isArray(codes) ? codes : []).map((c) =>
    typeof c === "string" ? { code: c, points: fallback.points, expires_at: fallback.expires_at || null, drop_id: fallback.drop_id || null } : c
  );
}

// First array found under any of the given keys (list endpoints are read
// defensively so a renamed list key does not blank the table).
export function listOf(res, keys) {
  for (const k of keys) if (Array.isArray(res?.[k])) return res[k];
  return Array.isArray(res) ? res : [];
}

// Map an API error to a readable sentence. Known error codes get copy; the
// raw code is shown only as a fallback.
const ERROR_COPY = {
  "payout-unverified": "The backend could not verify this payout on-chain yet.",
  "not-payable": "This claim is no longer payable.",
  "not-cancellable": "This claim can no longer be cancelled.",
  "claim-not-found": "Claim not found.",
  "code-exists": "That code already exists.",
  "rewards-unavailable": "The rewards service is unavailable. Try again shortly.",
  "rewards-upstream-unreachable": "The rewards service could not be reached. Try again shortly.",
  "internal-error": "The rewards service hit an error. Try again shortly.",
  forbidden: "You do not have permission for this action.",
};
export function apiError(err, fallback) {
  const data = err?.data || err?.response?.data;
  const code = data?.error || data?.message;
  const copy = code && ERROR_COPY[code];
  if (copy) return data?.reason ? `${copy} (${data.reason})` : copy;
  if (code && typeof code === "string") return code;
  if (err?.status === 403) return ERROR_COPY.forbidden;
  return (typeof err === "string" ? err : err?.message) || fallback;
}
